import type {
  BillingPackageDto,
  ContinueOnFreeResult,
  PaymentInvoiceDto,
  PricingQuote,
  ShopifyCheckoutDto,
} from '../models/billing.types'

import { ApiError, appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

type BillingHttp = Pick<typeof appHttp, 'get' | 'post'>

const FEATURE_OWNED = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
} as const

function malformedBillingResponse(detail: string, payload: unknown): never {
  throw new ApiError({
    kind: 'unknown',
    status: null,
    code: null,
    fields: {},
    payload,
    userVisible: true,
    detail,
  })
}

export class BillingApi {
  constructor(private readonly http: BillingHttp = appHttp) {}

  async listPricingPackages(): Promise<BillingPackageDto[]> {
    const response = await this.http.get<ApiEnvelope<BillingPackageDto[]>>(
      '/payment/pricing-packages',
      FEATURE_OWNED,
    )
    return unwrapEnvelope(response)
  }

  async createShopifyCheckout(
    workspaceId: number,
    packageId: number,
    offerCode?: string,
  ): Promise<ShopifyCheckoutDto> {
    const body: { plan_id: number; workspace_id: number; offerCode?: string } = {
      plan_id: packageId,
      workspace_id: workspaceId,
    }
    if (offerCode) body.offerCode = offerCode

    const response = await this.http.post<ShopifyCheckoutDto>(
      `/payment/${workspaceId}/shopify/billing/create`,
      body,
      FEATURE_OWNED,
    )
    return unwrap(response)
  }

  async getLatestPricingQuote(accountId: number): Promise<PricingQuote> {
    const response = await this.http.get<ApiEnvelope<PricingQuote>>(
      `/accounts/${accountId}/pricing/quote/latest/`,
      FEATURE_OWNED,
    )
    return unwrapEnvelope(response)
  }

  async listInvoices(accountId: number): Promise<PaymentInvoiceDto[]> {
    const response = await this.http.get<{ invoices?: PaymentInvoiceDto[] }>(
      `/payment/invoices/${accountId}`,
      FEATURE_OWNED,
    )
    const payload = unwrap(response)
    if (!Array.isArray(payload.invoices)) {
      malformedBillingResponse('Malformed invoice response', payload)
    }
    return payload.invoices
  }

  async cancelSubscription(accountId: number, packageId: number): Promise<void> {
    const response = await this.http.get<{ success?: boolean }>(
      `/payment/cancel-subscription/${accountId}`,
      { ...FEATURE_OWNED, params: { package_id: packageId } },
    )
    const payload = unwrap(response)
    if (payload.success !== true) {
      malformedBillingResponse('Subscription was not cancelled', payload)
    }
  }

  async continueOnFree(accountId: number): Promise<ContinueOnFreeResult> {
    const response = await this.http.post<ApiEnvelope<ContinueOnFreeResult>>(
      `/payment/continue-on-free/${accountId}`,
      undefined,
      FEATURE_OWNED,
    )
    return unwrapEnvelope(response)
  }
}

export const billingApi = new BillingApi()
